import { z } from 'zod';

import {
    FRESHNESS_BUCKETS,
    SCOPE_TYPES,
    type ScopeType,
    SORT_FIELDS,
    VOLATILITY_VALUES,
} from '@apps/web/features/admin_agent_scratchpad/model/constants';

const optionalTrimmedString = z
    .string()
    .transform((value) => value.trim())
    .pipe(z.string().min(1))
    .optional()
    .catch(undefined);

const scopeTypeSearchParam = z
    .union([z.enum(SCOPE_TYPES), z.array(z.enum(SCOPE_TYPES))])
    .transform((value) =>
        Array.from(new Set(Array.isArray(value) ? value : [value])),
    );

const tagSearchParam = z
    .union([z.string().min(1), z.array(z.string().min(1))])
    .transform((value) =>
        Array.from(new Set(Array.isArray(value) ? value : [value])),
    );

/**
 * Zod schema for `/admin/agent/scratchpad` search params. Every filter that
 * the UI can round-trip lives here so a shared URL restores the exact view.
 * `scope_type` and `tag` are multi-select. TanStack's default parser emits a
 * scalar for a single query value and an array for repeated values, so both
 * forms are normalized at this boundary.
 * `cross_scope` is the explicit opt-in the API requires to span every org.
 */
export const scratchpadSearchSchema = z.object({
    page: z.coerce.number().int().min(1).catch(1),
    scope_type: scopeTypeSearchParam.optional().catch(undefined),
    scope_id: optionalTrimmedString,
    tag: tagSearchParam.optional().catch(undefined),
    volatility: z.enum(VOLATILITY_VALUES).optional().catch(undefined),
    freshness: z.enum(FRESHNESS_BUCKETS).optional().catch(undefined),
    include_retired: z.coerce.boolean().optional().catch(false),
    cross_scope: z.coerce.boolean().optional().catch(false),
    search: optionalTrimmedString,
    sort: z.enum(SORT_FIELDS).optional().catch(undefined),
    direction: z.enum(['asc', 'desc']).optional().catch(undefined),
});

export type ScratchpadSearch = z.infer<typeof scratchpadSearchSchema>;

export type ScratchpadSearchPatch = Omit<
    Partial<ScratchpadSearch>,
    'scope_type'
> & {
    readonly scope_type?: ScopeType | Array<ScopeType>;
};

/** Detail-view schema — carries the filter context back so back-nav restores the list slice. */
export const scratchpadDetailSearchSchema = scratchpadSearchSchema.extend({
    revisions_page: z.coerce.number().int().min(1).catch(1),
});

export type ScratchpadDetailSearch = z.infer<
    typeof scratchpadDetailSearchSchema
>;

export function normalizeScopeTypes(
    search: ScratchpadSearch,
): ReadonlyArray<ScopeType> {
    return search.scope_type ?? [];
}

/**
 * Uses the same URL representation as account deep-links: a scalar for one
 * scope kind and a repeated-value array only when multiple kinds are needed.
 */
export function serializeScopeTypes(
    scopeTypes: ReadonlyArray<ScopeType>,
): ScopeType | Array<ScopeType> | undefined {
    if (scopeTypes.length === 0) {
        return undefined;
    }
    if (scopeTypes.length === 1) {
        return scopeTypes[0];
    }
    return [...scopeTypes];
}

export function normalizeTags(search: ScratchpadSearch): ReadonlyArray<string> {
    return search.tag ?? [];
}

/**
 * The single non-global scope kind currently selected, or null when the
 * selection is empty, only `global`, or spans more than one non-global kind.
 * `scope_id` is one string per URL, so it only makes sense to bind it when
 * exactly one non-global kind is chosen — the UI reveals the id picker in
 * that case and hides it otherwise.
 */
export function singleNonGlobalScopeKind(
    search: ScratchpadSearch,
): Exclude<ScopeType, 'global'> | null {
    const specific = (search.scope_type ?? []).filter(
        (kind): kind is Exclude<ScopeType, 'global'> => kind !== 'global',
    );
    if (specific.length !== 1) {
        return null;
    }
    return specific[0] ?? null;
}

/**
 * Mirrors `_enforce_scope_policy` in `services/api/routes/admin/agent_scratchpad.py`
 * exactly. Returning `false` prevents the hook from firing a request the API
 * is guaranteed to 422; every case here matches an HTTPException there:
 *
 *   1. `scope_id` requires ≥1 `scope_type`.
 *   2. `global + scope_id` is rejected (regardless of `cross_scope`).
 *   3. `cross_scope=false` requires ≥1 `scope_type`.
 *   4. `cross_scope=false` with any non-global `scope_type` requires `scope_id`.
 */
export function isScopeSelectionValid(search: ScratchpadSearch): boolean {
    const scopeTypes = search.scope_type ?? [];
    const scopeId = search.scope_id;
    if (scopeId !== undefined && scopeTypes.length === 0) {
        return false;
    }
    if (scopeTypes.includes('global') && scopeId !== undefined) {
        return false;
    }
    if (search.cross_scope === true) {
        return true;
    }
    if (scopeTypes.length === 0) {
        return false;
    }
    const hasNonGlobal = scopeTypes.some((kind) => kind !== 'global');
    if (hasNonGlobal && scopeId === undefined) {
        return false;
    }
    return true;
}
